import React from "react";
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  MenuItem,
  Popover,
  TextField,
  Typography,
} from "@mui/material";
import { Close, LanguageRounded } from "@mui/icons-material";
import InexButton from "../../../InexButton";
import { useAuthToken } from "../../../../hooks/auth";
import {
  useSetWebsiteMutation
} from "../../../../store/api/user";
import { useIsMobile } from "../../../../hooks/responsive";
import dayjs from 'dayjs';
import { DemoContainer } from '@mui/x-date-pickers/internals/demo';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar';
import { fr } from 'date-fns/locale';


const WebsiteDialog = ({ open, onClose, website }) => {
  const isMobile = useIsMobile();

  const [currentWebsite, setCurrentWebsite] = React.useState(website);

  const diff = React.useMemo(() => {
    return currentWebsite !== website;
  }, [website, currentWebsite]);

  const authToken = useAuthToken();

  const [setWebsiteMutation] = useSetWebsiteMutation();

  const handleValidate = async () => {
    // 1. On gère le "skip" manuellement
    if (!authToken) {
      console.warn("Action annulée : Aucun token d'authentification.");
      return;
    }

    try {
      // 2. On déclenche la mutation
      // .unwrap() permet de tomber dans le catch en cas d'erreur (403, 504, etc.)
      await setWebsiteMutation({
        authToken,
        website: currentWebsite,
      }).unwrap();

      // 3. Succès : On ferme la modale
      onClose();

    } catch (err) {
      // 4. Gestion des erreurs Cloud Run
      console.error("Échec de la mutation :", err);
    }
  };

  return (
    <Dialog
      open={open}
      sx={{
        backgroundColor: (theme) => theme.palette.blackPro + "99",
      }}
      maxWidth="xl"
      PaperProps={{
        sx: {
          backgroundColor: (theme) => theme.palette.blackProLighter,
          color: "white",
          boxShadow: (theme) => theme.shadows[27],
          borderRadius: "15px",
          paddingLeft: isMobile ? 1 : 5,
          paddingRight: isMobile ? 1 : 5,
          width: isMobile ? '100%' : 'none',
        },
      }}
    >
      <DialogTitle
        sx={{ m: 0, p: 2, ml: 3, mr: 3, textAlign: "center" }}
        id="customized-dialog-title"
      >
        <Typography variant={"h3"}>
          {"Votre site internet"}
        </Typography>
      </DialogTitle>
      <IconButton
        aria-label="close"
        onClick={onClose}
        sx={(theme) => ({
          position: "absolute",
          right: 8,
          top: 8,
          color: theme.palette.grey[500],
        })}
      >
        <Close />
      </IconButton>
      <DialogContent>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            minWidth: isMobile ? "100%": "450px"
          }}
        >
          
                    <TextField
                      size={"small"}
                      placeholder={"ex: https://www.inexplore.com"}
                      sx={{
                        backgroundColor: "#C7C4DE",
                        borderRadius: "30px",
                        width: "100%",
                        outline: "none",
                      }}
                      value={currentWebsite}
                      onChange={(e) => {
                        setCurrentWebsite(e.target.value);
                      }}
                      variant="standard"
                      slotProps={{
                        input: {
                          sx: {
                            outline: "none",
                            padding: 1,
                            "&::before": {
                              display: "none",
                            },
                            "&::after": {
                              display: "none",
                            },
                          },
                          startAdornment: (
                            <InputAdornment position="start">
                              <LanguageRounded
                                sx={{
                                  marginLeft: 0.5,
                                }}
                              />
                            </InputAdornment>
                          ),
                        },
                      }}
                    />

        </Box>
      </DialogContent>
      <DialogActions>
        <InexButton
          text={"Annuler"}
          variant={"text"}
          textWithGradient={false}
          onClick={onClose}
          sx={{
            zIndex: 1,
            marginBottom: 3,
            marginTop: 3,
            height: "50px",
            minWidth: "100px",
            background: "white",
            color: "#202020",
            border: "none",
            "&:hover": {
              border: "none",
              backgroundColor: (theme) => theme.palette.primary.main,
              "& > p": {
                color: "white",
              },
            },
          }}
        />
        <InexButton
          text={"Valider"}
          variant={"text"}
          textWithGradient={false}
          onClick={handleValidate}
          disabled={!diff}
          sx={{
            zIndex: 1,
            marginBottom: 3,
            marginTop: 3,
            height: "50px",
            minWidth: "100px",
            background: (theme) =>
              !diff ? theme.palette.grey[400] : theme.palette.primary.main,
            color: "white",
            border: "none",
            "&:hover": {
              border: "none",
              backgroundColor: "white",
              "& > p": {
                color: (theme) => theme.palette.primary.main,
              },
            },
          }}
        />
      </DialogActions>
    </Dialog>
  );
};

export default WebsiteDialog;
